import { Box, Stack, Typography } from '@mui/material';
import { KeyboardEvent, ReactNode, useRef } from 'react';

import { Badge, BadgeVariants } from '@/components/notifications/Badge/Badge';
import { COLORS } from '@/styles/tokens/colors';

export interface VerticalTabProps<TValue extends string> {
  value: TValue;
  display: ReactNode;
  /**
   * Count shown as a success badge trailing the label. Omit (or pass 0) to
   * render no badge.
   */
  badgeCount?: number;
}

export interface VerticalTabsProps<TValue extends string> {
  tabs: VerticalTabProps<TValue>[];
  activeValue: TValue;
  onChange: (value: TValue) => void;
  /** Runs custom navigation behavior when a tab is activated by pointer or keyboard. */
  onTabClick?: (value: TValue) => void;
  /** Accessible name for the tab list. */
  ariaLabel: string;
}

/**
 * A left-hand tab rail: full-width rows whose active item takes a light gray
 * fill. Used where a step's content splits into sibling panes that shouldn't
 * each get their own step in a top-level tracker.
 *
 * Follows the ARIA tabs pattern: only the selected tab is in the tab order,
 * and the arrow keys move between tabs from there, selecting as they go.
 *
 * Distinct from `SidebarTab` (a taller, bold, right-bordered treatment used by
 * in-page navigation) and from `TabPanelList` (a horizontal, navy-filled rail).
 */
export function VerticalTabs<TValue extends string>({
  tabs,
  activeValue,
  onChange,
  onTabClick,
  ariaLabel,
}: VerticalTabsProps<TValue>) {
  const tabRefs = useRef<Partial<Record<TValue, HTMLDivElement | null>>>({});

  const activateTab = (value: TValue) => {
    onChange(value);
    onTabClick?.(value);
  };

  /**
   * Because only the selected tab is focusable, moving focus is what makes the
   * others reachable at all — the arrow keys select the target and follow it
   * with focus, so the two never drift apart.
   */
  const selectAndFocus = (value: TValue) => {
    activateTab(value);
    tabRefs.current[value]?.focus();
  };

  const handleKeyDown = (
    event: KeyboardEvent<HTMLDivElement>,
    index: number
  ) => {
    const lastIndex = tabs.length - 1;

    const targetIndex = (() => {
      switch (event.key) {
        case 'ArrowDown':
          return index === lastIndex ? 0 : index + 1;
        case 'ArrowUp':
          return index === 0 ? lastIndex : index - 1;
        case 'Home':
          return 0;
        case 'End':
          return lastIndex;
        default:
          return null;
      }
    })();

    if (targetIndex !== null) {
      event.preventDefault();
      const target = tabs[targetIndex];
      if (target) selectAndFocus(target.value);
      return;
    }

    // Selection already follows focus, so these only matter for a tab reached
    // by pointer or by assistive tech that moves focus without selecting.
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      const current = tabs[index];
      if (current) activateTab(current.value);
    }
  };

  return (
    <Stack
      role="tablist"
      aria-label={ariaLabel}
      aria-orientation="vertical"
      spacing={0.25}
    >
      {tabs.map(({ value, display, badgeCount }, index) => {
        const isActive = value === activeValue;

        return (
          <Box
            key={value}
            ref={(element: HTMLDivElement | null) => {
              tabRefs.current[value] = element;
            }}
            role="tab"
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            onClick={() => activateTab(value)}
            onKeyDown={(event) => handleKeyDown(event, index)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              px: 1.5,
              py: 1,
              borderRadius: '4px',
              cursor: 'pointer',
              backgroundColor: isActive ? COLORS.GRAY[100] : 'transparent',
              '&:hover': {
                backgroundColor: isActive
                  ? COLORS.GRAY[100]
                  : COLORS.FUNCTIONAL.HOVER,
              },
            }}
          >
            {/* Active and inactive differ by fill and text color, not weight. */}
            <Typography
              variant="body-s"
              sx={{ color: isActive ? undefined : COLORS.TEXT.SECONDARY }}
            >
              {display}
            </Typography>
            {!!badgeCount && (
              <Badge variant={BadgeVariants.Success} display={badgeCount} />
            )}
          </Box>
        );
      })}
    </Stack>
  );
}
